import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { collection, addDoc, getDocs, query, where, updateDoc, deleteDoc, doc, serverTimestamp, getDoc, setDoc, increment, orderBy } from 'firebase/firestore';

const REACTIONS = ['🔥', '💡', '👏', '🚀', '⭐'];
const CATEGORIES = ['Repetitive', 'Communication', 'Research', 'Synthesis'];
// Daily counts working days, not calendar days, so the yearly number stays honest.
const FREQ_PER_YEAR = { once: 1, daily: 260, weekly: 52, monthly: 12 };

// Turn "time saved per occurrence" + "how often" into annualized hours.
function annualizedHoursFrom(timeSaved, timeUnit, frequency) {
  const n = parseFloat(timeSaved);
  if (!n || n <= 0) return 0;
  const hoursEach = timeUnit === 'hours' ? n : n / 60;
  return Math.round(hoursEach * (FREQ_PER_YEAR[frequency] || 0) * 10) / 10;
}

export default function WinsWall({ user }) {
  const [wins, setWins] = useState([]);
  const [newWin, setNewWin] = useState('');
  const [anonymous, setAnonymous] = useState(false);
  const [timeSaved, setTimeSaved] = useState('');
  const [timeUnit, setTimeUnit] = useState('minutes');
  const [frequency, setFrequency] = useState('weekly');
  const [category, setCategory] = useState('');
  const [tool, setTool] = useState('');
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  // Unread tracking: one "last visited" timestamp per person. Anything posted
  // or replied to after that counts as new, so nothing has to be marked read.
  const [lastSeen, setLastSeen] = useState(null);
  const [openReplies, setOpenReplies] = useState({});
  const [repliesByWin, setRepliesByWin] = useState({});
  const [replyDrafts, setReplyDrafts] = useState({});
  const [replyBusy, setReplyBusy] = useState({});
  const [userProfile, setUserProfile] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      const profileSnap = await getDoc(doc(db, 'users', user.uid));
      if (profileSnap.exists()) {
        const profile = profileSnap.data();
        setUserProfile(profile);
        // Remember when they last looked BEFORE stamping this visit, so the
        // "New" markers reflect what changed since last time.
        const seen = profile.winsLastSeenAt?.toDate ? profile.winsLastSeenAt.toDate().getTime() : 0;
        setLastSeen(seen);
        await loadWins(profile);
        try {
          await setDoc(doc(db, 'users', user.uid), { winsLastSeenAt: serverTimestamp() }, { merge: true });
        } catch { /* badge will just clear on the next visit */ }
      }
      setLoading(false);
    };
    load();
  }, [user.uid]);

  // Wins are company-wide: everyone at the company sees them no matter which
  // program code they registered under. The companyCode pass is a safety net
  // for older wins saved before the company field existed.
  const loadWins = async (profile) => {
    const company = profile?.company || '';
    const companyCode = profile?.companyCode || '';
    if (!company && !companyCode) return;
    const found = new Map();
    const run = async (field, value) => {
      if (!value) return;
      try {
        const snap = await getDocs(query(collection(db, 'wins'), where(field, '==', value)));
        snap.docs.forEach(d => found.set(d.id, { id: d.id, ...d.data() }));
      } catch { /* one pass may be blocked; the other still returns results */ }
    };
    await run('company', company);
    await run('companyCode', companyCode);
    setWins([...found.values()].sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)));
  };

  const handlePost = async (e) => {
    e.preventDefault();
    if (!newWin.trim()) return;
    if (!userProfile?.companyCode) { setError('Your account is not linked to a company. Contact your program coordinator.'); return; }
    setPosting(true);
    try {
      const annualizedHours = annualizedHoursFrom(timeSaved, timeUnit, frequency);
      const fields = {
        text: newWin.trim(),
        anonymous,
        authorName: anonymous ? 'Anonymous' : (userProfile?.displayName || user.displayName || 'Team Member'),
        timeSaved: timeSaved ? parseFloat(timeSaved) : null,
        timeUnit,
        frequency,
        annualizedHours,
        category: category || null,
        tool: tool.trim() || null,
      };
      if (editingId) {
        await updateDoc(doc(db, 'wins', editingId), { ...fields, editedAt: serverTimestamp() });
      } else {
        await addDoc(collection(db, 'wins'), {
          ...fields,
          authorId: user.uid,
          companyCode: userProfile.companyCode,
          company: userProfile.company,
          reactions: { '🔥': [], '💡': [], '👏': [], '🚀': [], '⭐': [] },
          createdAt: serverTimestamp(),
        });
      }
      resetForm();
      await loadWins(userProfile);
    } catch {
      setError(editingId ? 'Could not save your changes. Please try again.' : 'Could not post your win. Please try again.');
    }
    setPosting(false);
  };

  const resetForm = () => {
    setNewWin('');
    setTimeSaved('');
    setTimeUnit('minutes');
    setFrequency('weekly');
    setCategory('');
    setTool('');
    setAnonymous(false);
    setEditingId(null);
    setError('');
  };

  // Load one of your own wins back into the form to edit it.
  const handleStartEdit = (win) => {
    setNewWin(win.text || '');
    setTimeSaved(win.timeSaved != null ? String(win.timeSaved) : '');
    setTimeUnit(win.timeUnit || 'minutes');
    setFrequency(win.frequency || 'weekly');
    setCategory(win.category || '');
    setTool(win.tool || '');
    setAnonymous(!!win.anonymous);
    setEditingId(win.id);
    setError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (win) => {
    if (!window.confirm('Delete this win? This cannot be undone, and it will be removed from your team’s ROI totals.')) return;
    try {
      await deleteDoc(doc(db, 'wins', win.id));
      if (editingId === win.id) resetForm();
      setWins(prev => prev.filter(w => w.id !== win.id));
    } catch {
      setError('Could not delete that win. Please try again.');
    }
  };

  const loadReplies = async (winId) => {
    try {
      const snap = await getDocs(query(collection(db, 'wins', winId, 'replies'), orderBy('createdAt', 'asc')));
      setRepliesByWin(prev => ({ ...prev, [winId]: snap.docs.map(d => ({ id: d.id, ...d.data() })) }));
    } catch {
      setRepliesByWin(prev => ({ ...prev, [winId]: [] }));
    }
  };

  const toggleReplies = async (win) => {
    const isOpen = !!openReplies[win.id];
    setOpenReplies(prev => ({ ...prev, [win.id]: !isOpen }));
    if (!isOpen && !repliesByWin[win.id]) await loadReplies(win.id);
  };

  const handlePostReply = async (win) => {
    const text = (replyDrafts[win.id] || '').trim();
    if (!text) return;
    setReplyBusy(prev => ({ ...prev, [win.id]: true }));
    try {
      await addDoc(collection(db, 'wins', win.id, 'replies'), {
        text,
        authorId: user.uid,
        authorName: userProfile?.displayName || user.displayName || 'Team Member',
        company: userProfile?.company || '',
        companyCode: userProfile?.companyCode || '',
        createdAt: serverTimestamp(),
      });
      // Keep a count and a timestamp on the win so the collapsed view and the
      // unread badge work without reading every reply.
      await updateDoc(doc(db, 'wins', win.id), { replyCount: increment(1), lastReplyAt: serverTimestamp() });
      setReplyDrafts(prev => ({ ...prev, [win.id]: '' }));
      setWins(prev => prev.map(w => w.id === win.id ? { ...w, replyCount: (w.replyCount || 0) + 1 } : w));
      await loadReplies(win.id);
    } catch {
      setError('Could not post your reply. Please try again.');
    }
    setReplyBusy(prev => ({ ...prev, [win.id]: false }));
  };

  const handleDeleteReply = async (winId, reply) => {
    if (!window.confirm('Delete this reply?')) return;
    try {
      await deleteDoc(doc(db, 'wins', winId, 'replies', reply.id));
      await updateDoc(doc(db, 'wins', winId), { replyCount: increment(-1) });
      setWins(prev => prev.map(w => w.id === winId ? { ...w, replyCount: Math.max(0, (w.replyCount || 1) - 1) } : w));
      setRepliesByWin(prev => ({ ...prev, [winId]: (prev[winId] || []).filter(r => r.id !== reply.id) }));
    } catch {
      setError('Could not delete that reply. Please try again.');
    }
  };

  const handleReact = async (winId, emoji) => {
    const winRef = doc(db, 'wins', winId);
    const win = wins.find(w => w.id === winId);
    if (!win) return;
    const current = win.reactions?.[emoji] || [];
    const adding = !current.includes(user.uid);
    const updated = adding ? [...current, user.uid] : current.filter(id => id !== user.uid);
    // Stamp the time a like lands so the author can be told about it. Taking a
    // like back does not count as news.
    await updateDoc(winRef, {
      [`reactions.${emoji}`]: updated,
      ...(adding ? { lastReactionAt: serverTimestamp() } : {}),
    });
    setWins(prev => prev.map(w => w.id === winId ? { ...w, reactions: { ...w.reactions, [emoji]: updated } } : w));
  };

  const millis = (ts) => (ts?.seconds ? ts.seconds * 1000 : 0);

  // New since their last visit. Their own win is only news when someone else
  // replied to it or liked it.
  const isNewActivity = (win) => {
    if (!lastSeen) return false;
    const posted = millis(win.createdAt);
    const replied = millis(win.lastReplyAt);
    const liked = millis(win.lastReactionAt);
    if (win.authorId === user.uid) return Math.max(replied, liked) > lastSeen;
    return Math.max(posted, replied) > lastSeen;
  };

  // What to tell the author on their own card: replies matter more than likes.
  const ownActivityLabel = (win) => {
    if (!lastSeen || win.authorId !== user.uid) return '';
    const replied = millis(win.lastReplyAt) > lastSeen;
    const liked = millis(win.lastReactionAt) > lastSeen;
    if (replied && liked) return 'New reply and new likes on your win';
    if (replied) return 'Someone replied to your win';
    if (liked) return 'Someone liked your win';
    return '';
  };

  const formatDate = (ts) => {
    if (!ts?.toDate) return '';
    return ts.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (loading) return <div style={{ color: '#8A8A8A', fontSize: '14px' }}>Loading Wins Wall...</div>;

  return (
    <div style={{ maxWidth: '640px' }}>
      <div style={styles.heading}>Wins Wall</div>
      <div style={styles.sub}>Share what is working. Inspire your team. No scores, no rankings. Just real wins.</div>

      {!userProfile?.companyCode && (
        <div style={styles.noCode}>Your account is not linked to a company code. Contact your program coordinator to get access to the Wins Wall.</div>
      )}

      {userProfile?.companyCode && (
        <form onSubmit={handlePost} style={styles.postBox}>
          <div style={styles.postLabel}>{editingId ? 'Edit your win' : 'Share a Win'}</div>
          <textarea
            style={styles.postInput}
            value={newWin}
            onChange={e => setNewWin(e.target.value)}
            placeholder="What did you accomplish with AI this week? A time save, a better output, a workflow that changed? Share it here."
            maxLength={400}
          />

          <div style={styles.roiHint}>Add the numbers to show your impact (optional, but it powers your team's ROI report)</div>
          <div style={styles.roiRow}>
            <div style={styles.roiField}>
              <label style={styles.roiLabel}>Time saved</label>
              <div style={{ display: 'flex', gap: '6px' }}>
                <input type="number" min="0" style={{ ...styles.roiInput, width: '70px' }} value={timeSaved} onChange={e => setTimeSaved(e.target.value)} placeholder="30" />
                <select style={styles.roiSelect} value={timeUnit} onChange={e => setTimeUnit(e.target.value)}>
                  <option value="minutes">minutes</option>
                  <option value="hours">hours</option>
                </select>
              </div>
            </div>
            <div style={styles.roiField}>
              <label style={styles.roiLabel}>How often</label>
              <select style={styles.roiSelect} value={frequency} onChange={e => setFrequency(e.target.value)}>
                <option value="once">Just once</option>
                <option value="daily">Every day</option>
                <option value="weekly">Every week</option>
                <option value="monthly">Every month</option>
              </select>
            </div>
            <div style={styles.roiField}>
              <label style={styles.roiLabel}>Type of work</label>
              <select style={styles.roiSelect} value={category} onChange={e => setCategory(e.target.value)}>
                <option value="">—</option>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div style={styles.roiField}>
              <label style={styles.roiLabel}>Tool used</label>
              <input type="text" style={{ ...styles.roiInput, width: '120px' }} value={tool} onChange={e => setTool(e.target.value)} placeholder="e.g. Claude" />
            </div>
          </div>
          {annualizedHoursFrom(timeSaved, timeUnit, frequency) > 0 && (
            <div style={styles.roiPreview}>⏱ That is about <strong>{annualizedHoursFrom(timeSaved, timeUnit, frequency)} hours a year</strong> saved.</div>
          )}

          <div style={styles.postFooter}>
            <label style={styles.anonLabel}>
              <input type="checkbox" checked={anonymous} onChange={e => setAnonymous(e.target.checked)} style={{ marginRight: '6px', accentColor: '#1B2A4A' }} />
              Post anonymously
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={styles.charCount}>{newWin.length}/400</span>
              {editingId && (
                <button type="button" style={styles.cancelBtn} onClick={resetForm} disabled={posting}>Cancel</button>
              )}
              <button style={{ ...styles.postBtn, opacity: newWin.trim() ? 1 : 0.5 }} type="submit" disabled={posting || !newWin.trim()}>
                {posting ? (editingId ? 'Saving...' : 'Posting...') : (editingId ? 'Save changes' : 'Post Win →')}
              </button>
            </div>
          </div>
          {error && <div style={styles.error}>{error}</div>}
        </form>
      )}

      <div style={styles.winsList}>
        {wins.length === 0 && (
          <div style={styles.empty}>No wins posted yet. Be the first to share something.</div>
        )}
        {wins.map(win => (
          <div key={win.id} style={{ ...styles.winCard, ...(isNewActivity(win) ? styles.winCardNew : {}) }}>
            {isNewActivity(win) && (
              <div style={styles.newFlag}>{ownActivityLabel(win) || 'New'}</div>
            )}
            <div style={styles.winText}>{win.text}</div>
            {(win.annualizedHours > 0 || win.category || win.tool) && (
              <div style={styles.winBadges}>
                {win.annualizedHours > 0 && <span style={styles.roiBadge}>⏱ ~{win.annualizedHours} hrs/year saved</span>}
                {win.category && <span style={styles.metaBadge}>{win.category}</span>}
                {win.tool && <span style={styles.metaBadge}>{win.tool}</span>}
              </div>
            )}
            <div style={styles.winMeta}>
              <div style={styles.winAuthor}>{win.anonymous ? 'Anonymous' : win.authorName}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {win.authorId === user.uid && (
                  <>
                    <button style={styles.editBtn} onClick={() => handleStartEdit(win)}>Edit</button>
                    <button style={styles.deleteLink} onClick={() => handleDelete(win)}>Delete</button>
                  </>
                )}
                <div style={styles.winDate}>{formatDate(win.createdAt)}{win.editedAt ? ' · edited' : ''}</div>
              </div>
            </div>
            <div style={styles.reactRow}>
              {REACTIONS.map(emoji => {
                const count = win.reactions?.[emoji]?.length || 0;
                const reacted = win.reactions?.[emoji]?.includes(user.uid);
                return (
                  <button key={emoji} style={{ ...styles.reactBtn, ...(reacted ? styles.reactBtnActive : {}) }} onClick={() => handleReact(win.id, emoji)}>
                    {emoji} {count > 0 && <span style={styles.reactCount}>{count}</span>}
                  </button>
                );
              })}
              <button style={styles.replyToggle} onClick={() => toggleReplies(win)}>
                {openReplies[win.id] ? 'Hide replies' : (win.replyCount ? `💬 ${win.replyCount} ${win.replyCount === 1 ? 'reply' : 'replies'}` : '💬 Reply')}
              </button>
            </div>

            {openReplies[win.id] && (
              <div style={styles.replyPanel}>
                {(repliesByWin[win.id] || []).map(r => (
                  <div key={r.id} style={styles.replyItem}>
                    <div style={styles.replyText}>{r.text}</div>
                    <div style={styles.replyMeta}>
                      <span>{r.authorName}</span>
                      <span>{formatDate(r.createdAt)}</span>
                      {r.authorId === user.uid && (
                        <button style={styles.deleteLink} onClick={() => handleDeleteReply(win.id, r)}>Delete</button>
                      )}
                    </div>
                  </div>
                ))}
                {(repliesByWin[win.id] || []).length === 0 && (
                  <div style={styles.replyEmpty}>No replies yet. Be the first to cheer this on.</div>
                )}
                <div style={styles.replyForm}>
                  <input
                    style={styles.replyInput}
                    type="text"
                    placeholder="Write a reply..."
                    maxLength={300}
                    value={replyDrafts[win.id] || ''}
                    onChange={e => setReplyDrafts(prev => ({ ...prev, [win.id]: e.target.value }))}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handlePostReply(win); } }}
                  />
                  <button
                    style={{ ...styles.replyBtn, opacity: (replyDrafts[win.id] || '').trim() ? 1 : 0.5 }}
                    onClick={() => handlePostReply(win)}
                    disabled={replyBusy[win.id] || !(replyDrafts[win.id] || '').trim()}
                  >{replyBusy[win.id] ? 'Posting...' : 'Reply'}</button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const styles = {
  heading: { fontFamily: 'Playfair Display, serif', fontSize: '26px', fontWeight: '900', color: '#1B2A4A', marginBottom: '4px' },
  sub: { fontSize: '13px', color: '#8A8A8A', marginBottom: '28px', fontWeight: '300' },
  noCode: { background: '#fdecea', color: '#a32d2d', padding: '16px 20px', fontSize: '13px', marginBottom: '24px' },
  postBox: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '24px', marginBottom: '24px' },
  postLabel: { fontFamily: 'Playfair Display, serif', fontSize: '16px', color: '#1B2A4A', marginBottom: '12px' },
  postInput: { width: '100%', border: '1.5px solid #E8E6E2', padding: '12px 14px', fontFamily: 'DM Sans, sans-serif', fontSize: '13px', color: '#3D3D3D', background: '#FAF8F5', outline: 'none', resize: 'vertical', minHeight: '100px', fontWeight: '300', lineHeight: '1.6', marginBottom: '12px' },
  postFooter: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  anonLabel: { fontSize: '12px', color: '#8A8A8A', display: 'flex', alignItems: 'center', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  charCount: { fontSize: '11px', color: '#C0C0C0' },
  postBtn: { background: '#1B2A4A', color: '#fff', border: 'none', padding: '9px 20px', fontFamily: 'Playfair Display, serif', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
  cancelBtn: { background: '#F2F0EC', color: '#1B2A4A', border: '1.5px solid #E8E6E2', padding: '8px 16px', fontFamily: 'DM Sans, sans-serif', fontSize: '12px', fontWeight: '600', cursor: 'pointer' },
  editBtn: { background: 'none', border: 'none', padding: 0, color: '#185fa5', fontSize: '11px', fontWeight: '600', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', textDecoration: 'underline' },
  deleteLink: { background: 'none', border: 'none', padding: 0, color: '#a32d2d', fontSize: '11px', fontWeight: '600', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', textDecoration: 'underline' },
  error: { background: '#fdecea', color: '#a32d2d', padding: '10px 14px', fontSize: '12px', marginTop: '8px' },
  winsList: { display: 'flex', flexDirection: 'column', gap: '12px' },
  empty: { textAlign: 'center', padding: '40px 20px', color: '#8A8A8A', fontSize: '13px', background: '#FFFFFF', border: '1.5px solid #E8E6E2' },
  winCard: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '20px 24px', position: 'relative' },
  winCardNew: { borderColor: '#D4A847', boxShadow: '0 0 0 3px rgba(212,168,71,0.12)' },
  newFlag: { position: 'absolute', top: '-9px', left: '20px', background: '#D4A847', color: '#1B2A4A', fontSize: '10px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', padding: '2px 10px' },
  replyToggle: { background: 'none', border: 'none', padding: '4px 6px', marginLeft: 'auto', color: '#185fa5', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  replyPanel: { marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #F2F0EC' },
  replyItem: { padding: '8px 0', borderBottom: '1px solid #FAF8F5' },
  replyText: { fontSize: '13px', color: '#3D3D3D', lineHeight: '1.6', fontWeight: '300' },
  replyMeta: { display: 'flex', gap: '12px', alignItems: 'center', fontSize: '11px', color: '#8A8A8A', marginTop: '3px' },
  replyEmpty: { fontSize: '12px', color: '#8A8A8A', fontStyle: 'italic', padding: '6px 0 10px' },
  replyForm: { display: 'flex', gap: '8px', marginTop: '10px' },
  replyInput: { flex: 1, border: '1.5px solid #E8E6E2', padding: '8px 12px', fontFamily: 'DM Sans, sans-serif', fontSize: '13px', color: '#3D3D3D', background: '#FAF8F5', outline: 'none' },
  replyBtn: { background: '#1B2A4A', color: '#fff', border: 'none', padding: '8px 18px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  winText: { fontSize: '14px', color: '#3D3D3D', lineHeight: '1.7', marginBottom: '12px', fontWeight: '300' },
  roiHint: { fontSize: '11px', color: '#8A8A8A', margin: '4px 0 10px', fontStyle: 'italic' },
  roiRow: { display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '8px' },
  roiField: { display: 'flex', flexDirection: 'column', gap: '4px' },
  roiLabel: { fontSize: '10px', fontWeight: '600', letterSpacing: '0.5px', textTransform: 'uppercase', color: '#8A8A8A' },
  roiInput: { border: '1.5px solid #E8E6E2', padding: '7px 10px', fontFamily: 'DM Sans, sans-serif', fontSize: '13px', color: '#3D3D3D', background: '#FAF8F5', outline: 'none', boxSizing: 'border-box' },
  roiSelect: { border: '1.5px solid #E8E6E2', padding: '7px 10px', fontFamily: 'DM Sans, sans-serif', fontSize: '13px', color: '#3D3D3D', background: '#FAF8F5', outline: 'none' },
  roiPreview: { fontSize: '12px', color: '#3b6d11', background: '#eaf3de', padding: '8px 12px', marginBottom: '12px' },
  winBadges: { display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' },
  roiBadge: { fontSize: '11px', fontWeight: '600', background: '#1B2A4A', color: '#D4A847', padding: '4px 10px', borderRadius: '3px' },
  metaBadge: { fontSize: '11px', fontWeight: '600', background: '#F2F0EC', color: '#1B2A4A', padding: '4px 10px', borderRadius: '3px' },
  winMeta: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' },
  winAuthor: { fontSize: '12px', fontWeight: '600', color: '#1B2A4A' },
  winDate: { fontSize: '11px', color: '#8A8A8A' },
  reactRow: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  reactBtn: { background: '#F2F0EC', border: '1.5px solid #E8E6E2', padding: '5px 10px', fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', borderRadius: '4px' },
  reactBtnActive: { background: '#1B2A4A', borderColor: '#1B2A4A' },
  reactCount: { fontSize: '11px', fontWeight: '600', color: '#8A8A8A' },
};
